import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { api } from "../api";
import { MaskedLine, SectionReveal } from "../components/Reveal";
import Marquee from "../components/Marquee";
import ProductCard from "../components/ProductCard";

const HERO_IMG =
  "https://images.unsplash.com/photo-1701651153899-b6d58587a161?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwyfHxoaWdoJTIwZmFzaGlvbiUyMG1vZGVsJTIwZWRpdG9yaWFsJTIwc3RyZWV0d2VhciUyMGxvb2tib29rfGVufDB8fHx8MTc4OTk1MzM1NHww&ixlib=rb-4.1.0&q=85";
const BANNER_IMG =
  "https://images.unsplash.com/photo-1779912217680-b7b449e62237?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHw0fHxoaWdoJTIwZmFzaGlvbiUyMG1vZGVsJTIwZWRpdG9yaWFsJTIwc3RyZWV0d2VhciUyMGxvb2tib29rfGVufDB8fHx8MTc4OTk1MzM1NHww&ixlib=rb-4.1.0&q=85";

const CHAPTERS = [
  { n: "01", title: "Purpose Over Mass", body: "Every silhouette is argued for. If a seam, pocket or panel cannot justify its existence, it is cut — literally. We release twelve pieces a year, not twelve hundred." },
  { n: "02", title: "Material Rigor", body: "Japanese selvedge, full-grain aniline leather, bonded technical shells. We source from four mills we can name, and we visit them every season." },
  { n: "03", title: "Archive, Not Trend", body: "Trends expire on a schedule. The archive does not. Each piece is numbered and documented, designed to be reissued — never replaced." },
  { n: "04", title: "Built to Outlast", body: "Reinforced stress points, repairable construction, lifetime mending on every garment. The most sustainable piece is the one you never throw away." },
];

export default function Home() {
  const [products, setProducts] = useState([]);
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 800], [0, 200]);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data.slice(0, 4))).catch(() => {});
  }, []);

  return (
    <main data-testid="home-page">
      <section data-testid="hero-section" className="relative h-screen overflow-hidden flex items-end">
        <motion.div style={{ y }} className="absolute inset-0 -bottom-48">
          <img src={HERO_IMG} alt="Edition 2026 lookbook" className="w-full h-full object-cover opacity-60" />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C] via-[#0A0A0C]/30 to-transparent" />
        <div className="relative z-10 px-4 sm:px-8 lg:px-16 pb-24 w-full">
          <MaskedLine delay={0.1}>
            <span className="mono text-xs tracking-[0.35em] text-[#FF334B] uppercase">Noir Archive // Edition 2026</span>
          </MaskedLine>
          <h1 className="font-display font-extrabold tracking-tighter uppercase leading-[0.95] text-4xl sm:text-5xl lg:text-6xl mt-6">
            <MaskedLine delay={0.3}>Defining the</MaskedLine>
            <MaskedLine delay={0.45}>
              <span>
                New <span className="font-editorial italic font-light normal-case text-[#FF334B]">silhouette</span>
              </span>
            </MaskedLine>
          </h1>
          <MaskedLine delay={0.7} className="mt-6 max-w-md text-zinc-400 text-sm sm:text-base font-light leading-relaxed">
            Twelve numbered pieces. Four mills. Zero compromise. The 2026 edition is a study in restraint.
          </MaskedLine>
          <motion.div
            className="flex flex-wrap gap-4 mt-10"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8 }}
          >
            <Link
              to="/shop"
              data-testid="hero-explore-button"
              className="bg-[#FF334B] text-white px-8 py-4 mono text-xs tracking-[0.25em] uppercase hover:bg-[#d92339] transition-colors flex items-center gap-3"
            >
              Explore Catalog <ArrowRight size={14} />
            </Link>
            <a
              href="#manifesto"
              data-testid="hero-manifesto-button"
              className="border border-zinc-600 px-8 py-4 mono text-xs tracking-[0.25em] uppercase hover:border-[#FF334B] hover:text-[#FF334B] transition-colors"
            >
              Discover Manifesto
            </a>
          </motion.div>
        </div>
      </section>

      <Marquee items={["Raw textures", "Uncompromising form", "Archival craft", "Edition 2026", "Numbered pieces"]} />

      <section data-testid="featured-products-section" className="px-4 sm:px-8 lg:px-16 py-20 lg:py-28">
        <SectionReveal>
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="mono text-xs tracking-[0.25em] text-[#FF334B] uppercase">Selected Works</p>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight uppercase mt-3">Featured Pieces</h2>
            </div>
            <Link to="/shop" data-testid="view-all-products-link" className="mono text-xs tracking-[0.25em] uppercase text-zinc-400 hover:text-[#FF334B] transition-colors hidden sm:flex items-center gap-2">
              View All <ArrowRight size={14} />
            </Link>
          </div>
        </SectionReveal>
        <div className="grid grid-cols-12 gap-6">
          {products.map((p, i) => (
            <SectionReveal
              key={p.slug}
              delay={i * 0.1}
              className={`col-span-12 sm:col-span-6 ${i % 4 === 0 || i % 4 === 3 ? "lg:col-span-7" : "lg:col-span-5"}`}
            >
              <ProductCard product={p} />
            </SectionReveal>
          ))}
        </div>
      </section>

      <section id="manifesto" data-testid="manifesto-section" className="px-4 sm:px-8 lg:px-16 py-20 lg:py-32 border-t border-zinc-900">
        <SectionReveal>
          <p className="mono text-xs tracking-[0.25em] text-[#FF334B] uppercase">The Manifesto</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight uppercase mt-3 mb-16">Four Chapters</h2>
        </SectionReveal>
        <div className="flex flex-col">
          {CHAPTERS.map((c, i) => (
            <SectionReveal key={c.n} delay={i * 0.05}>
              <div data-testid={`manifesto-chapter-${c.n}`} className="grid grid-cols-12 gap-6 py-10 border-t border-zinc-800/70 group">
                <p className="col-span-12 md:col-span-2 mono text-sm text-[#FF334B] tracking-[0.25em]">{c.n} /</p>
                <h3 className="col-span-12 md:col-span-4 font-display text-xl sm:text-2xl font-semibold uppercase tracking-tight group-hover:text-[#FF334B] transition-colors">
                  {c.title}
                </h3>
                <p className="col-span-12 md:col-span-6 text-zinc-400 leading-relaxed font-light text-sm sm:text-base">{c.body}</p>
              </div>
            </SectionReveal>
          ))}
        </div>
      </section>

      <section data-testid="editorial-banner" className="relative h-[70vh] overflow-hidden">
        <img src={BANNER_IMG} alt="Editorial lookbook" className="w-full h-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0C] via-transparent to-transparent" />
        <div className="absolute inset-0 flex items-center px-4 sm:px-8 lg:px-16">
          <SectionReveal className="max-w-2xl">
            <p className="font-editorial italic text-2xl sm:text-3xl lg:text-4xl text-zinc-200 leading-snug">
              "Luxury is not abundance. It is the discipline of the essential."
            </p>
            <p className="mono text-xs tracking-[0.25em] text-[#FF334B] uppercase mt-6">— Atelier Notes, 2026</p>
            <Link
              to="/shop"
              data-testid="banner-shop-button"
              className="inline-flex items-center gap-3 mt-10 border border-zinc-500 px-8 py-4 mono text-xs tracking-[0.25em] uppercase hover:border-[#FF334B] hover:text-[#FF334B] transition-colors"
            >
              Shop the Edition <ArrowRight size={14} />
            </Link>
          </SectionReveal>
        </div>
      </section>
    </main>
  );
}
